import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useStore';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Gift, Palette, Code, Share2, Copy, Check, Monitor, Tablet, Smartphone,
  ChevronDown, ExternalLink, QrCode, Download, Globe, CreditCard, Wallet,
  Mail, MessageCircle, Percent, DollarSign, Settings, Eye, Zap
} from 'lucide-react';

const DEFAULT_CONFIG = {
  primaryColor: '#F97316',
  backgroundColor: '#FFFFFF',
  textColor: '#1E293B',
  accentColor: '#FBBF24',
  theme: 'light',
  fontFamily: 'inter',
  borderRadius: 16,
  layout: 'cards',
  presetAmounts: [50, 100, 200, 500],
  allowCustomAmount: true,
  minCustomAmount: 50,
  maxCustomAmount: 5000,
  currency: 'MXN',
  currencySymbol: '$',
  cardTitle: 'Tarjetas de Regalo',
  cardSubtitle: 'El regalo perfecto para momentos especiales',
  buttonText: 'Comprar tarjeta',
  buttonStyle: 'filled',
  deliveryMethods: ['email', 'whatsapp'],
  paymentMethods: ['stripe', 'paypal', 'restaurant'],
  showLogo: true,
  showRestaurantName: true,
  showDeliveryOptions: true,
  showValidityDate: true,
  validityDays: 365,
  termsText: 'Válida por {days} días a partir de la fecha de compra. No reembolsable.',
  discountOnline: 0,
};

const CURRENCIES = [
  { code:'MXN', symbol:'$', label:'Peso Mexicano (MXN)' },
  { code:'USD', symbol:'$', label:'Dólar Americano (USD)' },
  { code:'EUR', symbol:'€', label:'Euro (EUR)' },
  { code:'COP', symbol:'$', label:'Peso Colombiano (COP)' },
  { code:'ARS', symbol:'$', label:'Peso Argentino (ARS)' },
  { code:'CLP', symbol:'$', label:'Peso Chileno (CLP)' },
];

const FONTS = [
  { id:'inter', label:'Inter (Moderno)' },
  { id:'poppins', label:'Poppins (Amigable)' },
  { id:'playfair', label:'Playfair Display (Elegante)' },
];

function GiftCardWidgetPreview({ config, restaurantName }) {
  const [selectedAmount, setSelectedAmount] = useState(config.presetAmounts[1] || 100);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [step, setStep] = useState(1);
  const [delivery, setDelivery] = useState(config.deliveryMethods[0] || 'email');
  const pc = config.primaryColor;
  const isDark = config.theme === 'dark';
  const br = `${config.borderRadius}px`;
  const ff = { inter:"'Inter',sans-serif", poppins:"'Poppins',sans-serif", playfair:"'Playfair Display',serif" }[config.fontFamily] || "'Inter',sans-serif";
  const sym = config.currencySymbol || '$';

  const amt = isCustom ? (parseFloat(customAmount)||0) : selectedAmount;
  const finalAmt = config.discountOnline > 0 ? (amt * (1 - config.discountOnline / 100)).toFixed(2) : amt;

  const btnFilled = config.buttonStyle === 'filled';
  const btn = { backgroundColor:btnFilled?pc:'transparent', color:btnFilled?'#fff':pc, border:btnFilled?'none':`2px solid ${pc}`, borderRadius:`calc(${br} - 4px)`, padding:'12px 24px', fontWeight:700, fontSize:14, cursor:'pointer', width:'100%', fontFamily:ff };

  const cardStyle = { backgroundColor: isDark?'#1e293b':'#fff', borderRadius:br, border:`1px solid ${isDark?'#334155':'#e2e8f0'}`, boxShadow:'0 2px 8px rgba(0,0,0,0.08)', overflow:'hidden' };

  return (
    <div style={{ backgroundColor:config.backgroundColor, color:config.textColor, fontFamily:ff, borderRadius:br, padding:24, minHeight:420, boxSizing:'border-box' }}>
      {step === 1 && (
        <>
          <div style={{ textAlign:'center', marginBottom:24 }}>
            <div style={{ fontSize:36, marginBottom:8 }}>🎁</div>
            <h2 style={{ fontWeight:800, fontSize:20, color:config.textColor, margin:'0 0 6px' }}>{config.cardTitle}</h2>
            <p style={{ fontSize:13, color:`${config.textColor}99`, margin:0 }}>{config.cardSubtitle}</p>
          </div>

          {/* Amount selection */}
          <div style={{ marginBottom:16 }}>
            <p style={{ fontSize:12, fontWeight:600, color:`${config.textColor}aa`, marginBottom:8, margin:'0 0 8px' }}>Elige el monto</p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10 }}>
              {config.presetAmounts.map(a => (
                <button key={a} onClick={()=>{setSelectedAmount(a);setIsCustom(false);}}
                  style={{ ...cardStyle, padding:'14px 12px', textAlign:'center', cursor:'pointer', border:`2px solid ${!isCustom&&selectedAmount===a?pc:'#e2e8f0'}`, background:!isCustom&&selectedAmount===a?`${pc}12`:'#fff' }}>
                  <div style={{ fontSize:18, fontWeight:800, color:!isCustom&&selectedAmount===a?pc:config.textColor }}>{sym}{a.toLocaleString()}</div>
                  <div style={{ fontSize:11, color:`${config.textColor}66` }}>{config.currency}</div>
                </button>
              ))}
              {config.allowCustomAmount && (
                <button onClick={()=>setIsCustom(true)}
                  style={{ ...cardStyle, padding:'14px 12px', textAlign:'center', cursor:'pointer', border:`2px solid ${isCustom?pc:'#e2e8f0'}`, background:isCustom?`${pc}12`:'#fff', gridColumn:'span 2' }}>
                  <div style={{ fontSize:14, fontWeight:700, color:isCustom?pc:config.textColor }}>Monto personalizado</div>
                  {isCustom && (
                    <input type="number" value={customAmount} onChange={e=>setCustomAmount(e.target.value)} placeholder={`${sym}${config.minCustomAmount} – ${sym}${config.maxCustomAmount}`}
                      style={{ marginTop:8, padding:'8px 12px', border:`1.5px solid ${pc}`, borderRadius:8, fontSize:13, textAlign:'center', width:'70%', outline:'none' }} onClick={e=>e.stopPropagation()} />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Delivery method */}
          {config.showDeliveryOptions && config.deliveryMethods.length > 1 && (
            <div style={{ marginBottom:16 }}>
              <p style={{ fontSize:12, fontWeight:600, color:`${config.textColor}aa`, margin:'0 0 8px' }}>Forma de entrega</p>
              <div style={{ display:'flex', gap:8 }}>
                {config.deliveryMethods.map(m => (
                  <button key={m} onClick={()=>setDelivery(m)}
                    style={{ flex:1, padding:'10px', border:`2px solid ${delivery===m?pc:'#e2e8f0'}`, borderRadius:10, background:delivery===m?`${pc}12`:'transparent', cursor:'pointer', fontSize:13, fontWeight:600, color:delivery===m?pc:config.textColor }}>
                    {m==='email'?'📧 Email':m==='whatsapp'?'💬 WhatsApp':'🖨️ Impresa'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Summary box */}
          <div style={{ background:`${pc}10`, borderRadius:12, padding:'12px 16px', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontSize:13, color:config.textColor }}>Total a pagar</span>
            <div style={{ textAlign:'right' }}>
              {config.discountOnline > 0 && <div style={{ fontSize:11, color:`${config.textColor}66`, textDecoration:'line-through' }}>{sym}{amt.toLocaleString()} {config.currency}</div>}
              <div style={{ fontSize:22, fontWeight:900, color:pc }}>{sym}{parseFloat(finalAmt).toLocaleString()} {config.currency}</div>
              {config.discountOnline > 0 && <div style={{ fontSize:11, color:'#22c55e', fontWeight:700 }}>−{config.discountOnline}% al pagar en línea</div>}
            </div>
          </div>

          <button style={btn} onClick={()=>setStep(2)}>{config.buttonText}</button>

          {config.showValidityDate && (
            <p style={{ textAlign:'center', fontSize:11, color:`${config.textColor}55`, marginTop:12, marginBottom:0 }}>
              {config.termsText.replace('{days}', config.validityDays)}
            </p>
          )}
        </>
      )}

      {step === 2 && (
        <>
          <div style={{ display:'flex', alignItems:'center', marginBottom:20 }}>
            <button onClick={()=>setStep(1)} style={{ background:'transparent', border:'none', cursor:'pointer', color:config.textColor, padding:'4px 8px 4px 0' }}>← Volver</button>
            <h3 style={{ fontWeight:700, fontSize:16, margin:0 }}>Datos del destinatario</h3>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {[
              {label:'Tu nombre (quien regala)', ph:'Nombre del remitente'},
              {label:'Nombre del destinatario', ph:'A quién va dirigida'},
              {label:'Email del destinatario', ph:'email@ejemplo.com', type:'email'},
              {label:'Mensaje personalizado', ph:'¡Disfruta esta experiencia!', multi:true},
            ].map((f,i)=>(
              <div key={i}>
                <label style={{ fontSize:12, fontWeight:600, color:`${config.textColor}aa`, display:'block', marginBottom:4 }}>{f.label}</label>
                {f.multi ? (
                  <textarea placeholder={f.ph} style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:13, resize:'none', minHeight:60, outline:'none', boxSizing:'border-box', fontFamily:ff }} />
                ) : (
                  <input type={f.type||'text'} placeholder={f.ph} style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:ff }} />
                )}
              </div>
            ))}
          </div>

          {/* Payment methods */}
          {config.paymentMethods.length > 0 && (
            <div style={{ marginTop:16 }}>
              <p style={{ fontSize:12, fontWeight:600, color:`${config.textColor}aa`, margin:'0 0 10px' }}>Método de pago</p>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {config.paymentMethods.includes('stripe') && (
                  <div style={{ padding:'12px 16px', border:'1.5px solid #e2e8f0', borderRadius:12, display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}>
                    <CreditCard size={18} color={pc} />
                    <span style={{ fontSize:13, fontWeight:600 }}>Tarjeta de crédito / débito</span>
                    <div style={{ marginLeft:'auto', display:'flex', gap:4 }}>
                      {['VISA','MC','AMEX'].map(b=><span key={b} style={{ fontSize:9, padding:'2px 5px', background:'#f1f5f9', borderRadius:4, fontWeight:700, color:'#475569' }}>{b}</span>)}
                    </div>
                  </div>
                )}
                {config.paymentMethods.includes('paypal') && (
                  <div style={{ padding:'12px 16px', border:'1.5px solid #e2e8f0', borderRadius:12, display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}>
                    <span style={{ fontSize:13, fontWeight:800, color:'#003087' }}>Pay</span><span style={{ fontSize:13, fontWeight:800, color:'#009cde' }}>Pal</span>
                    <span style={{ fontSize:13, fontWeight:600, marginLeft:4 }}>PayPal</span>
                  </div>
                )}
                {config.paymentMethods.includes('restaurant') && (
                  <div style={{ padding:'12px 16px', border:'1.5px solid #e2e8f0', borderRadius:12, display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}>
                    <Gift size={18} color={pc} />
                    <span style={{ fontSize:13, fontWeight:600 }}>Pagar en el restaurante</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <button style={{ ...btn, marginTop:20 }}>Confirmar y pagar {sym}{parseFloat(finalAmt).toLocaleString()}</button>
        </>
      )}

      <p style={{ textAlign:'center', fontSize:10, color:'#9ca3af', marginTop:16, marginBottom:0 }}>Powered by <strong style={{ color:'#f97316' }}>OkReserva</strong></p>
    </div>
  );
}

export default function GiftCardWidgetBuilder() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [activeTab, setActiveTab] = useState('design');
  const [copied, setCopied] = useState(null);
  const [viewport, setViewport] = useState('desktop');
  const [presetInput, setPresetInput] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(false);
    try {
      const res = await api.get('/restaurant');
      setRestaurant(res.data);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setC = (key, value) => setConfig(c => ({ ...c, [key]: value }));

  const restaurantId = restaurant?.slug || user?.restaurant_id;
  const base = 'https://okreserva.okgo.mx';
  const params = new URLSearchParams({
    color: config.primaryColor.replace('#', ''),
    bg: config.backgroundColor.replace('#', ''),
    text: config.textColor.replace('#', ''),
    theme: config.theme,
    font: config.fontFamily,
    radius: config.borderRadius,
    currency: config.currency,
    sym: config.currencySymbol,
    amounts: config.presetAmounts.join(','),
    allowCustom: config.allowCustomAmount ? '1' : '0',
    minAmt: config.minCustomAmount,
    maxAmt: config.maxCustomAmount,
    validity: config.validityDays,
    discount: config.discountOnline,
    delivery: config.deliveryMethods.join(','),
    payments: config.paymentMethods.join(','),
    btnText: encodeURIComponent(config.buttonText),
    btnStyle: config.buttonStyle,
    title: encodeURIComponent(config.cardTitle),
    subtitle: encodeURIComponent(config.cardSubtitle),
  });

  const widgetUrl = restaurantId ? `${base}/widget/gift-cards/${restaurantId}?${params}` : '';
  const iframeCode = widgetUrl ? `<iframe\n  src="${widgetUrl}"\n  width="100%"\n  height="700"\n  frameborder="0"\n  style="border-radius:${config.borderRadius}px;overflow:hidden;"\n  allow="payment"\n></iframe>` : '';

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      toast.success('Copiado al portapapeles');
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const addPresetAmount = () => {
    const v = parseInt(presetInput, 10);
    if (!v || v < 1) return;
    if (!config.presetAmounts.includes(v)) {
      setC('presetAmounts', [...config.presetAmounts, v].sort((a, b) => a - b));
    }
    setPresetInput('');
  };

  const tabs = [
    { id:'design', label:'Diseño', icon:<Palette size={15}/> },
    { id:'amounts', label:'Montos & Pago', icon:<DollarSign size={15}/> },
    { id:'integration', label:'Integración', icon:<Code size={15}/> },
    { id:'share', label:'Compartir', icon:<Share2 size={15}/> },
  ];

  const viewports = [
    { id:'desktop', icon:<Monitor size={15}/>, w:'100%' },
    { id:'tablet', icon:<Tablet size={15}/>, w:540 },
    { id:'mobile', icon:<Smartphone size={15}/>, w:390 },
  ];

  const inputStyle = { width:'100%', padding:'8px 10px', border:'1.5px solid #e5e7eb', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' };
  const labelStyle = { fontSize:12, fontWeight:600, color:'#6b7280', display:'block', marginBottom:6 };
  const sectionTitle = (t) => <p style={{ fontWeight:700, fontSize:13, color:'#374151', marginBottom:12, paddingBottom:6, borderBottom:'1px solid #f3f4f6' }}>{t}</p>;

  return (
    <div style={{ display:'flex', height:'100vh', overflow:'hidden', background:'#f9fafb', fontFamily:"'Inter',sans-serif" }}>
      {/* Left Panel */}
      <div style={{ width:360, minWidth:320, background:'#fff', borderRight:'1px solid #e5e7eb', display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {/* Header */}
        <div style={{ padding:'18px 20px', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#f97316,#fbbf24)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Gift size={18} color="#fff" />
            </div>
            <div style={{ flex:1 }}>
              <h1 style={{ fontWeight:800, fontSize:15, margin:0, color:'#111827' }}>Widget Tarjetas de Regalo</h1>
              <p style={{ fontSize:11, color:'#9ca3af', margin:0 }}>{restaurant?.name || 'Configura tu widget'}</p>
            </div>
            <button onClick={() => { localStorage.setItem(`widget_gc_config_${restaurantId}`, JSON.stringify(config)); toast?.success('Guardado'); }}
              style={{ padding:'6px 12px', background:'#f97316', border:'none', borderRadius:8, color:'#fff', fontWeight:700, fontSize:12, cursor:'pointer' }}>
              Guardar
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display:'flex', borderBottom:'1px solid #f3f4f6', padding:'0 8px' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={()=>setActiveTab(t.id)}
              style={{ flex:1, padding:'10px 4px', border:'none', background:'transparent', cursor:'pointer', fontSize:11, fontWeight:activeTab===t.id?700:500, color:activeTab===t.id?'#f97316':'#9ca3af', borderBottom:`2px solid ${activeTab===t.id?'#f97316':'transparent'}`, display:'flex', flexDirection:'column', alignItems:'center', gap:4, marginBottom:-1 }}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ flex:1, overflowY:'auto', padding:20 }}>
          {activeTab === 'design' && (
            <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
              {sectionTitle('Apariencia')}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {[
                  {key:'primaryColor',label:'Color primario'},
                  {key:'backgroundColor',label:'Fondo'},
                  {key:'textColor',label:'Texto'},
                  {key:'accentColor',label:'Acento'},
                ].map(f=>(
                  <div key={f.key}>
                    <label style={labelStyle}>{f.label}</label>
                    <div style={{ display:'flex', alignItems:'center', gap:8, border:'1.5px solid #e5e7eb', borderRadius:8, padding:'6px 8px' }}>
                      <input type="color" value={config[f.key]} onChange={e=>setC(f.key,e.target.value)} style={{ width:26, height:26, border:'none', padding:0, cursor:'pointer', background:'transparent' }} />
                      <span style={{ fontSize:12, fontFamily:'monospace', color:'#374151' }}>{config[f.key]}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label style={labelStyle}>Tema</label>
                <div style={{ display:'flex', gap:8 }}>
                  {['light','dark'].map(t=>(
                    <button key={t} onClick={()=>setC('theme',t)} style={{ flex:1, padding:'8px', border:`2px solid ${config.theme===t?'#f97316':'#e5e7eb'}`, borderRadius:8, background:config.theme===t?'#fff7ed':'#fff', cursor:'pointer', fontSize:12, fontWeight:600, color:config.theme===t?'#f97316':'#6b7280' }}>
                      {t==='light'?'☀️ Claro':'🌙 Oscuro'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Tipografía</label>
                <select value={config.fontFamily} onChange={e=>setC('fontFamily',e.target.value)} style={{ ...inputStyle }}>
                  {FONTS.map(f=><option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Border radius: {config.borderRadius}px</label>
                <input type="range" min={0} max={32} value={config.borderRadius} onChange={e=>setC('borderRadius',parseInt(e.target.value))} style={{ width:'100%' }} />
              </div>

              <div>
                <label style={labelStyle}>Estilo de botón</label>
                <div style={{ display:'flex', gap:8 }}>
                  {[{id:'filled',l:'Sólido'},{id:'outline',l:'Contorno'},{id:'ghost',l:'Fantasma'}].map(s=>(
                    <button key={s.id} onClick={()=>setC('buttonStyle',s.id)} style={{ flex:1, padding:'8px 4px', border:`2px solid ${config.buttonStyle===s.id?'#f97316':'#e5e7eb'}`, borderRadius:8, background:config.buttonStyle===s.id?'#fff7ed':'transparent', cursor:'pointer', fontSize:11, fontWeight:600, color:config.buttonStyle===s.id?'#f97316':'#6b7280' }}>{s.l}</button>
                  ))}
                </div>
              </div>

              {sectionTitle('Textos')}
              {[
                {key:'cardTitle', label:'Título principal'},
                {key:'cardSubtitle', label:'Subtítulo'},
                {key:'buttonText', label:'Texto del botón'},
              ].map(f=>(
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <input value={config[f.key]} onChange={e=>setC(f.key,e.target.value)} style={inputStyle} />
                </div>
              ))}

              <div>
                <label style={labelStyle}>Moneda</label>
                <select value={config.currency} onChange={e=>{const c=CURRENCIES.find(x=>x.code===e.target.value);setConfig(p=>({...p,currency:c.code,currencySymbol:c.symbol}));}} style={inputStyle}>
                  {CURRENCIES.map(c=><option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
            </div>
          )}

          {activeTab === 'amounts' && (
            <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
              {sectionTitle('Montos predefinidos')}
              <div style={{ display:'flex', gap:8 }}>
                <input value={presetInput} onChange={e=>setPresetInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addPresetAmount()} type="number" placeholder="Ej: 250" style={{ ...inputStyle, flex:1 }} />
                <button onClick={addPresetAmount} style={{ padding:'8px 14px', background:'#f97316', color:'#fff', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700 }}>+</button>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {config.presetAmounts.map(a=>(
                  <div key={a} style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 12px', background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:20 }}>
                    <span style={{ fontSize:13, fontWeight:700, color:'#f97316' }}>{config.currencySymbol}{a}</span>
                    <button onClick={()=>setC('presetAmounts',config.presetAmounts.filter(x=>x!==a))} style={{ background:'transparent', border:'none', cursor:'pointer', color:'#9ca3af', padding:0, lineHeight:1 }}>×</button>
                  </div>
                ))}
              </div>

              <div>
                <label style={{ ...labelStyle, display:'flex', alignItems:'center', gap:8 }}>
                  <input type="checkbox" checked={config.allowCustomAmount} onChange={e=>setC('allowCustomAmount',e.target.checked)} />
                  Permitir monto personalizado
                </label>
                {config.allowCustomAmount && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:10 }}>
                    <div>
                      <label style={labelStyle}>Monto mínimo</label>
                      <input type="number" value={config.minCustomAmount} onChange={e=>setC('minCustomAmount',parseInt(e.target.value))} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Monto máximo</label>
                      <input type="number" value={config.maxCustomAmount} onChange={e=>setC('maxCustomAmount',parseInt(e.target.value))} style={inputStyle} />
                    </div>
                  </div>
                )}
              </div>

              {sectionTitle('Métodos de pago aceptados')}
              {[
                { id:'stripe', label:'💳 Tarjeta (Stripe)', desc:'Visa, MC, AMEX' },
                { id:'paypal', label:'🅿️ PayPal', desc:'Cuenta PayPal' },
                { id:'restaurant', label:'🏪 En el restaurante', desc:'Pago presencial' },
              ].map(p => (
                <label key={p.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', border:'1.5px solid #e5e7eb', borderRadius:10, cursor:'pointer', background: config.paymentMethods.includes(p.id)?'#fff7ed':'#fff' }}>
                  <input type="checkbox" checked={config.paymentMethods.includes(p.id)} onChange={e=>{ setC('paymentMethods', e.target.checked?[...config.paymentMethods,p.id]:config.paymentMethods.filter(x=>x!==p.id)); }} />
                  <div><div style={{ fontWeight:600, fontSize:13 }}>{p.label}</div><div style={{ fontSize:11, color:'#9ca3af' }}>{p.desc}</div></div>
                </label>
              ))}

              <div>
                <label style={labelStyle}>Descuento por pago en línea: {config.discountOnline}%</label>
                <input type="range" min={0} max={20} value={config.discountOnline} onChange={e=>setC('discountOnline',parseInt(e.target.value))} style={{ width:'100%' }} />
                <p style={{ fontSize:11, color:'#6b7280', marginTop:4 }}>Los clientes que paguen en línea obtienen este descuento.</p>
              </div>

              {sectionTitle('Entrega y validez')}
              {[
                { id:'email', label:'📧 Correo electrónico' },
                { id:'whatsapp', label:'💬 WhatsApp' },
                { id:'print', label:'🖨️ Impresa en restaurante' },
              ].map(m => (
                <label key={m.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', border:'1.5px solid #e5e7eb', borderRadius:10, cursor:'pointer', background:config.deliveryMethods.includes(m.id)?'#f0fdf4':'#fff' }}>
                  <input type="checkbox" checked={config.deliveryMethods.includes(m.id)} onChange={e=>{ setC('deliveryMethods', e.target.checked?[...config.deliveryMethods,m.id]:config.deliveryMethods.filter(x=>x!==m.id)); }} />
                  <span style={{ fontWeight:600, fontSize:13 }}>{m.label}</span>
                </label>
              ))}

              <div>
                <label style={labelStyle}>Validez en días: {config.validityDays}</label>
                <input type="range" min={30} max={730} step={30} value={config.validityDays} onChange={e=>setC('validityDays',parseInt(e.target.value))} style={{ width:'100%' }} />
                <p style={{ fontSize:11, color:'#6b7280', marginTop:4 }}>{Math.round(config.validityDays/30)} meses de validez.</p>
              </div>
            </div>
          )}

          {activeTab === 'integration' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {sectionTitle('Código para tu sitio web')}
              <div style={{ background:'#1e293b', borderRadius:10, overflow:'hidden' }}>
                <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', borderBottom:'1px solid #334155' }}>
                  <span style={{ fontSize:11, color:'#94a3b8' }}>HTML</span>
                  <button onClick={()=>copyText(iframeCode,'iframe')} style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 10px', background:'#334155', border:'none', borderRadius:6, cursor:'pointer', fontSize:11, color:'#cbd5e1', fontWeight:600 }}>
                    {copied==='iframe'?<><Check size={12}/>Copiado</>:<><Copy size={12}/>Copiar</>}
                  </button>
                </div>
                <pre style={{ margin:0, padding:'12px 14px', fontSize:11, color:'#e2e8f0', overflowX:'auto', fontFamily:'monospace', whiteSpace:'pre-wrap', wordBreak:'break-all' }}>{iframeCode || '// Configura tu restaurante para generar el código'}</pre>
              </div>

              {sectionTitle('URL directa')}
              <div style={{ display:'flex', gap:8 }}>
                <input value={widgetUrl} readOnly style={{ ...inputStyle, fontSize:11, fontFamily:'monospace', flex:1 }} />
                <button onClick={()=>copyText(widgetUrl,'url')} style={{ padding:'8px 12px', background:'#f97316', color:'#fff', border:'none', borderRadius:8, cursor:'pointer' }}>{copied==='url'?<Check size={14}/>:<Copy size={14}/>}</button>
                {widgetUrl && <a href={widgetUrl} target="_blank" rel="noopener noreferrer" style={{ padding:'8px 12px', background:'#f1f5f9', border:'none', borderRadius:8, cursor:'pointer', display:'flex', alignItems:'center' }}><ExternalLink size={14} color="#374151"/></a>}
              </div>

              {sectionTitle('Instrucciones de configuración')}
              <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:10, padding:'14px 16px' }}>
                <p style={{ fontWeight:700, fontSize:13, color:'#92400e', marginBottom:8 }}>⚡ Integración con Stripe</p>
                <p style={{ fontSize:12, color:'#78350f', marginBottom:0, lineHeight:1.6 }}>Para activar pagos con tarjeta, ve a <strong>Configuración → Integraciones → Stripe</strong> y conecta tu cuenta. El widget procesará pagos automáticamente.</p>
              </div>
              <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10, padding:'14px 16px' }}>
                <p style={{ fontWeight:700, fontSize:13, color:'#1e40af', marginBottom:8 }}>🅿️ PayPal</p>
                <p style={{ fontSize:12, color:'#1e3a8a', marginBottom:0, lineHeight:1.6 }}>Conecta tu cuenta PayPal Business en <strong>Configuración → Integraciones → PayPal</strong>.</p>
              </div>
            </div>
          )}

          {activeTab === 'share' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {sectionTitle('Código QR')}
              {widgetUrl ? (
                <>
                  <div style={{ display:'flex', justifyContent:'center' }}>
                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(widgetUrl)}`} alt="QR" style={{ borderRadius:12, border:'1px solid #e5e7eb' }} />
                  </div>
                  <div style={{ display:'flex', gap:8 }}>
                    <a href={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(widgetUrl)}`} download="qr-gift-card.png" target="_blank" rel="noopener noreferrer" style={{ flex:1, padding:'10px', background:'#f1f5f9', border:'none', borderRadius:8, cursor:'pointer', textAlign:'center', fontWeight:600, fontSize:13, color:'#374151', textDecoration:'none', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                      <Download size={14}/> Descargar QR
                    </a>
                    <button onClick={()=>copyText(widgetUrl,'share')} style={{ flex:1, padding:'10px', background:'#f97316', border:'none', borderRadius:8, cursor:'pointer', fontWeight:700, fontSize:13, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                      {copied==='share'?<><Check size={14}/>Copiado</>:<><Copy size={14}/>Copiar URL</>}
                    </button>
                  </div>
                </>
              ) : (
                <p style={{ color:'#9ca3af', fontSize:13, textAlign:'center' }}>Configura tu restaurante para generar el QR.</p>
              )}

              {sectionTitle('Compartir en redes')}
              {[
                { label:'WhatsApp', emoji:'💬', color:'#25D366', msg:`¡Ya tenemos tarjetas de regalo online! Regala experiencias únicas en ${restaurant?.name||'nuestro restaurante'} 🎁` },
                { label:'Instagram', emoji:'📸', color:'#E1306C', msg:'Tarjetas de regalo disponibles en nuestro perfil. ¡El regalo perfecto! 🎁✨' },
              ].map(s=>(
                <div key={s.label} style={{ padding:'12px 16px', border:'1px solid #e5e7eb', borderRadius:12 }}>
                  <p style={{ fontWeight:700, fontSize:12, color:'#374151', margin:'0 0 6px' }}>{s.emoji} {s.label}</p>
                  <p style={{ fontSize:12, color:'#6b7280', marginBottom:8 }}>{s.msg}</p>
                  <button onClick={()=>copyText(s.msg,'msg_'+s.label)} style={{ padding:'6px 12px', border:'1px solid #e5e7eb', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:11, fontWeight:600 }}>Copiar mensaje</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Preview */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {/* Preview Toolbar */}
        <div style={{ padding:'12px 20px', background:'#fff', borderBottom:'1px solid #e5e7eb', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:13, fontWeight:600, color:'#374151', display:'flex', alignItems:'center', gap:6 }}><Eye size={15}/>Vista previa en vivo</span>
          <div style={{ display:'flex', gap:4 }}>
            {viewports.map(v=>(
              <button key={v.id} onClick={()=>setViewport(v.id)} style={{ padding:'6px 10px', border:'none', borderRadius:8, background:viewport===v.id?'#f97316':'transparent', color:viewport===v.id?'#fff':'#9ca3af', cursor:'pointer' }}>{v.icon}</button>
            ))}
          </div>
        </div>

        {/* Preview Area */}
        <div style={{ flex:1, overflowY:'auto', background:'#f1f5f9', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'32px 24px' }}>
          <div style={{ width: viewport==='desktop'?'100%':viewports.find(v=>v.id===viewport)?.w, maxWidth:'100%', transition:'width 0.3s', boxShadow:'0 8px 32px rgba(0,0,0,0.12)', borderRadius:config.borderRadius }}>
            <GiftCardWidgetPreview config={config} restaurantName={restaurant?.name} />
          </div>
        </div>
      </div>
    </div>
  );
}
